import React from "react";

import { Form } from "react-bootstrap";

import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldError,
  type FieldErrors,
  type FieldValues,
  type Path,
} from "react-hook-form";

import { ERROR_MESSAGES } from "@pages/register/hooks/errorResolver";
import { countries } from "@util/countries";

import { FormErrorText } from "./FormErrorText";
import MultiSelectDropdown from "./MultiSelectDropdown";

const countryOptions = countries.map((country) => ({
  value: country,
  label: country,
}));

interface CountryDropdownProps<TFieldValues extends FieldValues, TField extends Path<TFieldValues>> {
  fieldName: TField;
  control: Control<TFieldValues>;
  isDisabled: boolean;
  isRequired: boolean;
  errors?: FieldErrors<TFieldValues>;
  isMultiSelect?: boolean;
  isInvalid?: boolean;
}

const CountryDropdown = <TFieldValues extends FieldValues, TField extends Path<TFieldValues>>({
  fieldName,
  control,
  isDisabled,
  isRequired,
  errors,
  isMultiSelect = false,
  isInvalid,
}: CountryDropdownProps<TFieldValues, TField>) => {
  const renderMultiSelectDropdown = (field: ControllerRenderProps<TFieldValues, TField>) => (
    <MultiSelectDropdown field={field} isInvalid={isInvalid} options={countryOptions} placeholder="Select countries" />
  );

  const renderSingleSelectDropdown = (field: ControllerRenderProps<TFieldValues, TField>) => (
    <Form.Select
      {...field}
      disabled={isDisabled}
      isInvalid={!!errors?.[fieldName] || isInvalid}
      className={field.value && "custom-select-value-selected"}
    >
      <option hidden value="">
        Select a country
      </option>
      {countryOptions.map(({ label, value }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </Form.Select>
  );

  return (
    <Form.Group controlId="Country">
      {isRequired && !isMultiSelect && (
        <Form.Label>
          <span className="requiredField">*</span> Country
        </Form.Label>
      )}
      <Controller
        name={fieldName}
        control={control}
        rules={{
          required: isRequired ? ERROR_MESSAGES.mandatory : false,
          validate: (value) => {
            if (isMultiSelect && isRequired) {
              return value?.length > 0 || ERROR_MESSAGES.mandatory;
            }
            return true;
          },
        }}
        render={({ field }) => (isMultiSelect ? renderMultiSelectDropdown(field) : renderSingleSelectDropdown(field))}
      />
      <FormErrorText error={errors?.[fieldName] as FieldError} />
    </Form.Group>
  );
};

export default CountryDropdown;
